// ============================================================
// ChartCarousel - the frame both probability carousels share
// ============================================================
// A titled header with ◀ ▶, the slide area, a row of stat badges and
// the dot indicators; ← → walk the slides with wrap when it has focus.
// The attack and defence carousels differ only in their slides, their
// charts and their stats - and in their CSS module, which is passed in so
// each keeps its own look.

import { useCallback, useState, type KeyboardEvent, type ReactNode, type CSSProperties } from 'react';
import clsx from '../../utils/clsx';

type Styles = Record<string, string>;

interface Slide<K extends string> {
  key: K;
  label: string;
}

interface ChartCarouselProps<K extends string> {
  slides: readonly Slide<K>[];
  styles: Styles;
  /** 'empty' with nothing to show, 'computing' while it works, else the slides. */
  status: 'empty' | 'computing' | 'ready';
  emptyText: string;
  /** The slide area's content for this slide. */
  renderSlide: (key: K) => ReactNode;
  /** The stat badges for this slide. */
  renderStats: (key: K) => ReactNode;
}

/** Nothing to show and not working on it: empty; working, or not yet a result: computing. */
export function carouselStatus(result: unknown, loading: boolean | undefined): ChartCarouselProps<string>['status'] {
  if (result && !loading) return 'ready';
  return !result && !loading ? 'empty' : 'computing';
}

/** The current slide, wrap-around steps, and the ← → keys. */
function useSlides<K extends string>(slides: readonly Slide<K>[]) {
  const [slide, setSlide] = useState<K>(slides[0].key);
  const idx = slides.findIndex((s) => s.key === slide);
  const goPrev = useCallback(() => setSlide(slides[(idx - 1 + slides.length) % slides.length].key), [idx, slides]);
  const goNext = useCallback(() => setSlide(slides[(idx + 1) % slides.length].key), [idx, slides]);
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const step = { ArrowLeft: goPrev, ArrowRight: goNext }[e.key];
      if (!step) return;
      e.preventDefault();
      step();
    },
    [goPrev, goNext],
  );
  return { slide, idx, setSlide, goPrev, goNext, onKeyDown };
}

export function ChartCarousel<K extends string>(props: ChartCarouselProps<K>) {
  const { slides, styles } = props;
  const { slide, idx, setSlide, goPrev, goNext, onKeyDown } = useSlides(slides);
  if (props.status !== 'ready') {
    return (
      <div className={styles.container}>
        {props.status === 'empty' ? (
          <div className={styles.empty}>{props.emptyText}</div>
        ) : (
          <div className={styles.computing}>Computing...</div>
        )}
      </div>
    );
  }
  return (
    // A focusable region the arrow keys page through, beside its own Prev/Next buttons.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions -- see above
    <div className={styles.container} tabIndex={0} onKeyDown={onKeyDown}>
      <div className={styles.header}>
        <button className={styles.navBtn} onClick={goPrev} aria-label="Previous chart">
          &#9664;
        </button>
        <span className={styles.title}>{slides[idx].label}</span>
        <button className={styles.navBtn} onClick={goNext} aria-label="Next chart">
          &#9654;
        </button>
      </div>
      <div className={styles.slideArea}>{props.renderSlide(slide)}</div>
      <div className={styles.statsRow}>{props.renderStats(slide)}</div>
      <div className={styles.dots}>
        {slides.map((s, i) => (
          <button
            key={s.key}
            className={clsx(styles.dot, i === idx && styles.dotActive)}
            onClick={() => setSlide(s.key)}
            aria-label={s.label}
          />
        ))}
      </div>
    </div>
  );
}

interface StatBadgeProps {
  styles: Styles;
  label: string;
  children: ReactNode;
  valueStyle?: CSSProperties;
}

/** One summary stat: its label and value. */
export function StatBadge({ styles, label, children, valueStyle }: StatBadgeProps) {
  return (
    <span className={styles.statBadge}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue} style={valueStyle}>
        {children}
      </span>
    </span>
  );
}
